import type { ArcAngles } from './PieSlice.utils';

export const LINE_RADIAL_LENGTH = 16;
export const LINE_HORIZONTAL_LENGTH = 24;
export const POLYLINE_LABEL_WIDTH = 120;
const MIN_POLYLINE_LABEL_ANGLE = 0.02;

export const POLYLINE_LABEL_ROW_HEIGHT = 18;
export const POLYLINE_LABEL_ROW_GAP = 2;

const POLYLINE_REFERENCE_RADIUS = 90;
const POLYLINE_MIN_SCALE = 0.4;
export const POLYLINE_HIDE_RADIUS = 40;

export const computePolylineScale = (radius: number): number =>
  Math.min(1, Math.max(POLYLINE_MIN_SCALE, radius / POLYLINE_REFERENCE_RADIUS));

export const computePolylinePoints = (angles: ArcAngles, sliceOuterRadius: number, adjustedRadius: number, scale = 1) => {
  const midAngle = (angles.startAngle + angles.endAngle) / 2;
  const cosA = Math.cos(midAngle - Math.PI / 2);
  const sinA = Math.sin(midAngle - Math.PI / 2);
  const scaledRadialLength = LINE_RADIAL_LENGTH * scale;
  const startX = cosA * sliceOuterRadius;
  const startY = sinA * sliceOuterRadius;
  const elbowX = cosA * (adjustedRadius + scaledRadialLength);
  const elbowY = sinA * (adjustedRadius + scaledRadialLength);
  return { startX, startY, elbowX, elbowY };
};

export const computePolylineLabelPosition = (
  angles: ArcAngles,
  labelRadius: number,
  selected: boolean,
  isHover: boolean,
  scale: number
) => {
  const midAngle = (angles.startAngle + angles.endAngle) / 2;
  const angleSpan = angles.endAngle - angles.startAngle;
  const cosA = Math.cos(midAngle - Math.PI / 2);
  const scaledRadialLength = LINE_RADIAL_LENGTH * scale;
  const scaledHorizontalLength = LINE_HORIZONTAL_LENGTH * scale;
  const elbowX = cosA * (labelRadius + scaledRadialLength);
  const elbowY = Math.sin(midAngle - Math.PI / 2) * (labelRadius + scaledRadialLength);
  const sign = cosA >= 0 ? 1 : -1;
  const labelX = elbowX + sign * scaledHorizontalLength;
  const offsetX = (sign * POLYLINE_LABEL_WIDTH) / 2;
  const showLabel = angleSpan >= MIN_POLYLINE_LABEL_ANGLE || selected || isHover;
  return { labelX, labelY: elbowY, offsetX, offsetY: 0, showLabel, labelRadius };
};

const POLYLINE_LABEL_BLOCK_HEIGHT = POLYLINE_LABEL_ROW_HEIGHT * 2 + POLYLINE_LABEL_ROW_GAP * 2;

export type PolylineLabelCandidate = {
  id: string;
  labelY: number;
  side: 'left' | 'right';
  value: number;
};

export const computePolylineLabelCandidate = (
  id: string,
  angles: ArcAngles,
  radius: number,
  value: number,
  scale: number
): PolylineLabelCandidate => {
  const midAngle = (angles.startAngle + angles.endAngle) / 2;
  const labelY = Math.sin(midAngle - Math.PI / 2) * (radius + LINE_RADIAL_LENGTH * scale);
  const side = Math.cos(midAngle - Math.PI / 2) >= 0 ? 'right' : 'left';
  return { id, labelY, side, value };
};

export const resolveVisiblePolylineLabels = (
  candidates: PolylineLabelCandidate[],
  highlightedId?: string,
  selectedIds?: Set<string>,
  blockHeight = POLYLINE_LABEL_BLOCK_HEIGHT
): Set<string> => {
  const priority = (id: string) => {
    if (id === highlightedId) return 0;
    if (selectedIds?.has(id)) return 1;
    return 2;
  };

  const byPriority = [...candidates].sort((a, b) => {
    const pa = priority(a.id);
    const pb = priority(b.id);
    if (pa !== pb) return pa - pb;
    return b.value - a.value;
  });

  const visible = new Set<string>();
  const keptByY: { left: number[]; right: number[] } = { left: [], right: [] };

  byPriority.forEach(({ id, labelY, side }) => {
    const kept = keptByY[side];
    if (kept.every(y => Math.abs(y - labelY) >= blockHeight)) {
      visible.add(id);
      kept.push(labelY);
    }
  });

  return visible;
};
